import { CanvasData, ImageData, Point, Triangle } from "@/types";
import { clearCanvas, getImageData } from "@/tools/canvas";

const pi = (a: Point, b: Point) => {
    return Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2);
};

export const getLongestSide = (triangle: Triangle) => {
    // return two Points
    interface Side {
        l: number;
        points: [Point, Point];
    }
    const sides: Side[] = [];
    sides.push({
        l: pi(triangle.A, triangle.B),
        points: [triangle.A, triangle.B],
    });
    sides.push({
        l: pi(triangle.A, triangle.C),
        points: [triangle.A, triangle.C],
    });
    sides.push({
        l: pi(triangle.B, triangle.C),
        points: [triangle.B, triangle.C],
    });
    sides.sort(function (a, b) {
        return b.l - a.l;
    });
    return sides[0].points;
};

export const splitTriangle = (
    triangle: Triangle,
    newPoint: Point,
    oldPoint1: Point,
    oldPoint2: Point
) => {
    let outerPoint1;
    if (triangle.A !== oldPoint1 && triangle.A !== oldPoint2) {
        outerPoint1 = triangle.A;
    } else if (triangle.B !== oldPoint1 && triangle.B !== oldPoint2) {
        outerPoint1 = triangle.B;
    } else {
        outerPoint1 = triangle.C;
    }
    const a: Triangle = {
        A: outerPoint1,
        B: oldPoint2,
        C: newPoint,
        color: triangle.color,
        deviation: 0,
        pixels: [],
        active: true,
        fresh: true,
    };
    const b: Triangle = {
        A: outerPoint1,
        B: oldPoint1,
        C: newPoint,
        color: triangle.color,
        deviation: 0,
        pixels: [],
        active: true,
        fresh: true,
    };

    return [a, b];
};

export const updateDeviation = (
    triangle: Triangle,
    imageData: ImageData,
    correction = 100
) => {
    let deviation = 0;
    const l = triangle.pixels.length;
    for (const pixelIndex of triangle.pixels) {
        deviation += Math.abs(imageData[pixelIndex] - triangle.color[0]);
        deviation += Math.abs(imageData[pixelIndex + 1] - triangle.color[1]);
        deviation += Math.abs(imageData[pixelIndex + 2] - triangle.color[2]);
    }
    triangle.deviation = deviation / (1 + l / correction);
};

export const updateColor = (triangle: Triangle, imageData: ImageData) => {
    const l = triangle.pixels.length;
    let red = 0,
        green = 0,
        blue = 0;
    // count total red, green and blue of the area of the triangle in the original data
    for (const pixelIndex of triangle.pixels) {
        red += imageData[pixelIndex];
        green += imageData[pixelIndex + 1];
        blue += imageData[pixelIndex + 2];
    }

    // find the average red, green and blue
    const r = Math.round(red / l);
    const g = Math.round(green / l);
    const b = Math.round(blue / l);
    triangle.color = [r, g, b];
};

export const updatePixels = (triangle: Triangle, canvasData: CanvasData) => {
    const context = canvasData.context;
    if (context) {
        // this function finds the pixels corresponding with the triangle by
        // drawing the triangle black on white, outputting the imagedata of that
        // and then finding the pixels with color value < 255
        clearCanvas(canvasData);

        context.fillStyle = "rgba(0,0,0,1)";
        context.beginPath();
        context.moveTo(triangle.A.x, triangle.A.y);
        context.lineTo(triangle.B.x, triangle.B.y);
        context.lineTo(triangle.C.x, triangle.C.y);
        context.closePath();
        context.fill();
        // find the black pixels
        const bits = canvasData.size.width * canvasData.size.height * 4;
        const pixels = [];
        const triangleData = getImageData(canvasData);
        for (let i = 0; i < bits; i += 4) {
            if (triangleData[i] < 255) {
                pixels.push(i);
            }
        }
        triangle.pixels = pixels;
    }
};
